import React from "react";
import { cn } from "../../styles";
import { StyledContainerProps } from "../../props";

export interface CenteredLayoutProps extends StyledContainerProps {
  /**
   * Controls the vertical centering behavior.
   * When true, content is positioned slightly above mathematical center for better visual balance.
   * When false, content is positioned at exact mathematical center.
   * @default true
   */
  slightlyAboveCenter?: boolean;
}

/**
 * CenteredLayout component for vertically and horizontally centering content within its container.
 * The component fills the full height of its parent container and uses flexible spacing
 * to position content in the center. By default, content is positioned slightly above
 * mathematical center (using grow-[1.5] for bottom space vs grow for top space) which
 * is more visually pleasing and follows design best practices.
 *
 * Ideal for empty states, loading screens, error pages, onboarding flows, or any content
 * that needs prominent centering within a page or container area.
 */
export const CenteredLayout = ({
  children,
  className,
  slightlyAboveCenter = true,
}: CenteredLayoutProps) => {
  const bottomSpacing = slightlyAboveCenter ? "grow-[1.5]" : "grow";

  return (
    <div className={cn("h-full flex flex-col", className)}>
      <div className="grow" />
      <div className="flex items-center justify-center">{children}</div>
      <div className={bottomSpacing} />
    </div>
  );
};

CenteredLayout.displayName = "CenteredLayout";
